import React from 'react';
import { useNavigate } from 'react-router-dom';
import VerifiedBadge from './VerifiedBadge';
import './ProfilePreview.css';

const ProfilePreview = ({ profile, onClose }) => {
  const navigate = useNavigate();

  if (!profile) return null;

  const handleEditProfile = () => {
    onClose();
    // Stay on profile page, just close preview
  };

  return (
    <div className="profile-preview-overlay" onClick={onClose}>
      <div className="profile-preview-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="preview-header">
          <div className="preview-banner">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 5C7.02944 5 3 9.02944 3 14C3 18.9706 7.02944 23 12 23C16.9706 23 21 18.9706 21 14C21 9.02944 16.9706 5 12 5Z" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 1V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span>Preview Mode - This is how others see your profile</span>
          </div>
          
          <div className="preview-actions">
            <button className="btn-edit-profile" onClick={handleEditProfile}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Edit Profile
            </button>
            <button className="btn-close-preview" onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Profile Content */}
        <div className="preview-content">
          {/* Cover Photo */}
          {profile.coverPhotoUrl && (
            <div className="preview-cover">
              <img src={profile.coverPhotoUrl} alt="Cover" />
            </div>
          )}

          {/* Profile Header */}
          <div className="preview-profile-header">
            <div className="preview-avatar">
              {profile.photos && profile.photos.length > 0 ? (
                <img src={profile.photos[0]} alt={profile.name} />
              ) : (
                <div className="preview-avatar-placeholder">
                  {profile.name?.charAt(0) || '?'}
                </div>
              )}
            </div>
            
            <div className="preview-profile-info">
              <h1>
                {profile.name || 'Anonymous'}
                {profile.age && <span className="preview-age">, {profile.age}</span>}
                {profile.hasVerifiedBadge && <VerifiedBadge />}
              </h1>
              {profile.location && (
                <div className="preview-location">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  {profile.location}
                </div>
              )}
            </div>
          </div>

          {/* Bio */}
          {profile.bio && (
            <div className="preview-section">
              <h3>About</h3>
              <p className="preview-bio">{profile.bio}</p>
            </div>
          )}

          {/* Photos */}
          {profile.photos && profile.photos.length > 0 && (
            <div className="preview-section">
              <h3>Photos ({profile.photos.length})</h3>
              <div className="preview-photos-grid">
                {profile.photos.map((photo, index) => (
                  <div key={index} className="preview-photo">
                    <img src={photo} alt={`Photo ${index + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Details */}
          <div className="preview-section">
            <h3>Details</h3>
            <div className="preview-details">
              {profile.height && (
                <div className="detail-item">
                  <span className="detail-label">Height:</span>
                  <span className="detail-value">{profile.height}</span>
                </div>
              )}
              {profile.bodyType && (
                <div className="detail-item">
                  <span className="detail-label">Body Type:</span>
                  <span className="detail-value">{profile.bodyType}</span>
                </div>
              )}
              {profile.ethnicity && (
                <div className="detail-item">
                  <span className="detail-label">Ethnicity:</span>
                  <span className="detail-value">{profile.ethnicity}</span>
                </div>
              )}
            </div>
          </div>

          {/* Interests */}
          {profile.interests && (
            <div className="preview-section">
              <h3>Interests</h3>
              <p className="preview-interests">{profile.interests}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePreview;
